import { useCallback, useLayoutEffect, useState } from 'react';

// from usehooks-ts lib
// https://usehooks-ts.com/react-hook/use-element-size

interface Size {
  width: number;
  height: number;
  scrollHeight: number;
  scrollWidth: number;
}

function useElementSize<T extends HTMLElement = HTMLDivElement>(): [
  (node: T | null) => void,
  Size,
  T | null
] {
  // Mutable values like 'ref.current' aren't valid dependencies
  // because mutating them doesn't re-render the component.
  // Instead, we use a state as a ref to be reactive.
  const [ref, setRef] = useState<T | null>(null);
  const [size, setSize] = useState<Size>({
    width: 0,
    height: 0,
    scrollHeight: 0,
    scrollWidth: 0,
  });

  const handleSize = useCallback(() => {
    if (ref) {
      setSize({
        width: ref.offsetWidth,
        height: ref.offsetHeight,
        scrollHeight: ref.scrollHeight,
        scrollWidth: ref.scrollWidth,
      });
    }
  }, [ref]);

  useLayoutEffect(() => {
    if (!ref) return;
    handleSize();
    const observer = new ResizeObserver(handleSize);
    observer.observe(ref);
    return () => observer.disconnect();
  }, [ref, handleSize]);

  return [setRef, size, ref];
}

export default useElementSize;
